/**
 * FleetCostsSummary — Sprint 88 iter61.
 * Vue budgétaire flotte multi-période (mois/trim/an) avec jauge budget annuel.
 */
import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { Fuel, Wrench, ShieldCheck, TrendingUp, TrendingDown, Settings, AlertTriangle, CheckCircle2, Car } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from "recharts";

const CAT_META = {
  fuel:        { label: "Carburant",  color: "#f59e0b", icon: <Fuel className="w-3 h-3" /> },
  maintenance: { label: "Entretien",  color: "#3b82f6", icon: <Wrench className="w-3 h-3" /> },
  insurance:   { label: "Assurance",  color: "#10b981", icon: <ShieldCheck className="w-3 h-3" /> },
  parking:     { label: "Parking",    color: "#a855f7" },
  tolls:       { label: "Péages",     color: "#ec4899" },
  other:       { label: "Autres",     color: "#64748b" },
};

const fmtEur = (n) => `${(Number(n) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €`;

const currentYear = () => new Date().getFullYear();

export default function FleetCostsSummary() {
  const [period, setPeriod] = useState("month");
  const [dateFrom, setDateFrom] = useState(`${currentYear()}-01`);
  const [dateTo, setDateTo] = useState(`${currentYear()}-12`);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [budgetOpen, setBudgetOpen] = useState(false);
  const [budget, setBudget] = useState({ total: 0, fuel: 0, maintenance: 0, insurance: 0, parking: 0, tolls: 0, other: 0 });

  const load = async () => {
    setLoading(true);
    try {
      const params = { period, from_: dateFrom, to_: dateTo };
      if (categoryFilter !== "all") params.category = categoryFilter;
      const r = await api.get("/fleet/costs-summary", { params });
      setSummary(r.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  const loadBudget = async () => {
    try {
      const r = await api.get("/fleet/settings/budget");
      setBudget(r.data.budget);
    } catch (e) { /* silent */ }
  };

  useEffect(() => { loadBudget(); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period, dateFrom, dateTo, categoryFilter]);

  const saveBudget = async () => {
    try {
      await api.put("/fleet/settings/budget", budget);
      toast.success("Budget annuel enregistré");
      setBudgetOpen(false);
      await load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const chartData = useMemo(() => summary?.series || [], [summary]);

  const totalVar = summary?.variation?.total;
  const fuelTotal = summary?.totals_by_category?.fuel || 0;
  const maintTotal = summary?.totals_by_category?.maintenance || 0;
  const grand = summary?.total || 0;

  return (
    <div className="space-y-6" data-testid="fleet-costs-summary-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "Manrope, sans-serif" }}>Budget & Coûts flotte</h1>
          <p className="text-sm text-slate-500">Analyse multi-période · {summary?.vehicle_count || 0} véhicules actifs</p>
        </div>
        <Button variant="outline" onClick={() => setBudgetOpen(true)} data-testid="open-budget-btn">
          <Settings className="w-4 h-4 mr-2" />Budgets annuels
        </Button>
      </div>

      {/* Filtres */}
      <Card>
        <CardContent className="p-4 flex flex-wrap gap-4 items-end">
          <div>
            <Label className="text-xs">Granularité</Label>
            <Tabs value={period} onValueChange={setPeriod} className="mt-1">
              <TabsList data-testid="period-tabs">
                <TabsTrigger value="month" data-testid="period-month">Mensuel</TabsTrigger>
                <TabsTrigger value="quarter" data-testid="period-quarter">Trimestriel</TabsTrigger>
                <TabsTrigger value="year" data-testid="period-year">Annuel</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <div>
            <Label className="text-xs">Du</Label>
            <Input type="month" value={dateFrom} onChange={e => setDateFrom(e.target.value)} data-testid="date-from" />
          </div>
          <div>
            <Label className="text-xs">Au</Label>
            <Input type="month" value={dateTo} onChange={e => setDateTo(e.target.value)} data-testid="date-to" />
          </div>
          <div className="min-w-[180px]">
            <Label className="text-xs">Catégorie</Label>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger data-testid="cat-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes catégories</SelectItem>
                {Object.entries(CAT_META).map(([k, m]) => (
                  <SelectItem key={k} value={k}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Total période" value={fmtEur(grand)} variation={totalVar} icon={<Car className="w-4 h-4" />} />
        <KpiCard label="Carburant" value={fmtEur(fuelTotal)} variation={summary?.variation?.fuel} icon={<Fuel className="w-4 h-4 text-amber-500" />} />
        <KpiCard label="Entretien" value={fmtEur(maintTotal)} variation={summary?.variation?.maintenance} icon={<Wrench className="w-4 h-4 text-blue-500" />} />
        <KpiCard label="Assurance + Autres" value={fmtEur(grand - fuelTotal - maintTotal)} icon={<ShieldCheck className="w-4 h-4 text-emerald-500" />} />
      </div>

      {/* Chart stacked */}
      <Card>
        <CardContent className="p-4">
          <p className="text-sm font-semibold mb-3">Évolution des coûts par catégorie</p>
          <div className="h-72">
            <ResponsiveContainer>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" fontSize={11} />
                <YAxis tickFormatter={v => `${(v/1000).toFixed(0)}k`} fontSize={11} />
                <Tooltip formatter={(v) => fmtEur(v)} />
                <Legend />
                {Object.entries(CAT_META).map(([k, m]) => (
                  <Bar key={k} dataKey={k} stackId="a" fill={m.color} name={m.label} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Budget gauge */}
      {summary?.budget_status && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold">Budget annuel {summary.budget_status.year}</p>
              <div className="flex items-center gap-2 text-xs">
                <span className="text-slate-500">Année écoulée à {summary.budget_status.burn_rate}%</span>
                {summary.budget_status.over_budget_projected ? (
                  <Badge className="bg-red-100 text-red-700"><AlertTriangle className="w-3 h-3 mr-1" />Dépassement projeté</Badge>
                ) : (
                  <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3 h-3 mr-1" />Budget tenu</Badge>
                )}
              </div>
            </div>
            <BudgetRow
              label="Total flotte"
              consumed={summary.budget_status.consumed_total}
              budget={summary.budget_status.annual_budget_total}
              projection={summary.budget_status.projection_end_year}
              over={summary.budget_status.over_budget_projected}
              highlight
            />
            <div className="mt-3 space-y-2">
              {summary.budget_status.per_category
                .filter(c => c.budget > 0 || c.consumed > 0)
                .map(c => (
                  <BudgetRow
                    key={c.category}
                    label={c.label}
                    consumed={c.consumed}
                    budget={c.budget}
                    projection={c.projection_end_year}
                    over={c.over_budget_projected}
                    color={CAT_META[c.category]?.color}
                  />
                ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top vehicles */}
      {summary && summary.top_vehicles?.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <p className="text-sm font-semibold mb-3">Top 10 véhicules dépensiers · {summary.from} → {summary.to}</p>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Plaque</TableHead>
                  <TableHead>Véhicule</TableHead>
                  <TableHead className="text-right">Total période</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {summary.top_vehicles.map(v => (
                  <TableRow key={v.vehicle_id} data-testid={`top-veh-${v.vehicle_id}`}>
                    <TableCell className="font-mono text-xs">{v.plate}</TableCell>
                    <TableCell className="text-sm">{[v.brand, v.model].filter(Boolean).join(" ") || "—"}</TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">{fmtEur(v.total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Budget dialog */}
      <Dialog open={budgetOpen} onOpenChange={setBudgetOpen}>
        <DialogContent className="max-w-lg" data-testid="budget-dialog">
          <DialogHeader>
            <DialogTitle>Budgets annuels flotte ({currentYear()})</DialogTitle>
            <DialogDescription>Fixez un budget cible pour l'année, total + par catégorie. Utilisé pour la jauge de suivi et la projection fin d'année.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Total flotte (€ HT)</Label>
              <Input type="number" min="0" value={budget.total}
                onChange={e => setBudget(b => ({ ...b, total: Number(e.target.value) }))}
                data-testid="budget-total" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(CAT_META).map(([k, m]) => (
                <div key={k}>
                  <Label className="flex items-center gap-1 text-xs">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: m.color }} />
                    {m.label}
                  </Label>
                  <Input type="number" min="0" value={budget[k]}
                    onChange={e => setBudget(b => ({ ...b, [k]: Number(e.target.value) }))}
                    data-testid={`budget-${k}`} />
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBudgetOpen(false)}>Annuler</Button>
            <Button onClick={saveBudget} data-testid="save-budget-btn">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KpiCard({ label, value, variation, icon }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-1.5 text-xs text-slate-500">{icon}{label}</div>
      <div className="text-2xl font-bold mt-1 tabular-nums">{value}</div>
      {variation !== null && variation !== undefined && (
        <div className={`text-xs mt-1 flex items-center gap-1 ${variation > 0 ? "text-red-600" : variation < 0 ? "text-emerald-600" : "text-slate-400"}`}>
          {variation > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          {variation > 0 ? "+" : ""}{variation}% vs période précédente
        </div>
      )}
    </div>
  );
}

function BudgetRow({ label, consumed, budget, projection, over, highlight, color }) {
  const pct = budget > 0 ? Math.min(100, (consumed / budget) * 100) : 0;
  const projPct = budget > 0 ? Math.min(150, (projection / budget) * 100) : 0;
  return (
    <div className={`${highlight ? "bg-slate-50 rounded-lg p-3" : "px-1"}`}>
      <div className="flex items-center justify-between mb-1 text-xs">
        <div className="flex items-center gap-1.5">
          {color && <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />}
          <span className={highlight ? "font-semibold text-sm" : ""}>{label}</span>
        </div>
        <div className="flex items-center gap-2 tabular-nums">
          <span className="font-semibold">{fmtEur(consumed)}</span>
          <span className="text-slate-400">/ {fmtEur(budget)}</span>
          {over && <span className="text-red-600 font-medium">Projection {fmtEur(projection)}</span>}
        </div>
      </div>
      <div className="relative">
        <Progress value={pct} className={`h-2 ${over ? "[&>div]:bg-red-500" : ""}`} />
        {budget > 0 && projection > 0 && projPct > pct && (
          <div className="absolute top-0 h-2 rounded-r bg-red-300/40 pointer-events-none"
                style={{ left: `${pct}%`, width: `${Math.min(projPct - pct, 100 - pct)}%` }} />
        )}
      </div>
    </div>
  );
}
